import { Badge, Button, Skeleton } from "@/shared/ui";
import { getAllocationColor } from "../../../utils";
import type { UseTimesheetAllocationsReturn } from "../../../hooks";
import WorkerAllocationModal from "./WorkerAllocationModal";

export default function TimesheetAllocationsSection(props: UseTimesheetAllocationsReturn) {
  const {
    workers, selectedWorker, openWorkerModal, closeWorkerModal,
    isLoadingAllocations, isDraft, canEdit, t,
  } = props;

  if (isLoadingAllocations) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="p-5 radius-lg border surface-base-stroke flex flex-col gap-3">
            <Skeleton width={100} height={20} borderRadius={10} />
            <Skeleton width={140} height={16} />
            <Skeleton height={8} borderRadius={4} />
            <Skeleton width={120} height={12} />
          </div>
        ))}
      </div>
    );
  }

  if (workers.length === 0) {
    return (
      <div className="p-6 text-center radius-lg border surface-base-stroke">
        <p className="text-body-regular-md content-base-secondary">{t("allocations.empty")}</p>
        <p className="text-body-regular-sm content-base-low mt-1">{t("allocations.emptyHint")}</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {workers.map((worker) => {
          const percent = worker.workHours > 0 ? Math.round((worker.totalAllocated / worker.workHours) * 100) : 0;
          const isMatched = worker.totalAllocated === worker.workHours && worker.workHours > 0;
          const hasMismatch = worker.totalAllocated > 0 && !isMatched;
          const hasNone = worker.totalAllocated === 0;
          const remaining = Math.max(0, worker.workHours - worker.totalAllocated);

          const isReadOnly = !(isDraft && canEdit);
          const cardContent = (
            <>
              <div className="flex flex-col gap-2 surface-component-stroke">
                <Badge
                  variant="soft"
                  color={isMatched ? "positive" : hasNone ? "gray" : "notice"}
                  text={
                    isMatched
                      ? t("allocations.status.matched")
                      : hasNone
                        ? t("allocations.status.none")
                        : t("allocations.status.mismatch")
                  }
                />

                <p className="text-body-bold-lg content-base-primary">{worker.workerName}</p>
              </div>

              <div className="flex flex-col gap-2 pt-3 border-t surface-component-stroke">
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5 flex-1">
                    {worker.allocations.map((alloc, i) => {
                      const pct = worker.workHours > 0 ? (parseFloat(alloc.hours) / worker.workHours) * 100 : 0;
                      return (
                        <div
                          key={alloc.id}
                          className="h-1.5 radius-full duration-300"
                          style={{ width: `${pct}%`, backgroundColor: getAllocationColor(i) }}
                        />
                      );
                    })}
                    <div className="h-1.5 radius-2xs surface-component-stroke-fill flex-1" />
                  </div>
                  <p className="text-label-md content-base-primary">{percent}%</p>
                </div>

                <p className="text-body-regular-sm text-alpha-black-50">
                  {t("allocations.cards.allocatedHoursOf", {
                    allocated: worker.totalAllocated,
                    total: worker.workHours,
                  })}
                </p>
              </div>

              {worker.allocations.length > 0 && (
                <div className="flex flex-col gap-2">
                  {worker.allocations.map((alloc, i) => (
                    <div key={alloc.id} className="flex items-center gap-1.5">
                      <span
                        className="w-1 h-5 rounded-full shrink-0"
                        style={{ backgroundColor: getAllocationColor(i) }}
                      />
                      <span className="text-body-regular-md content-action-neutral truncate">
                        {alloc.project_name} —{" "}
                        <span className="text-label-md">
                          {parseFloat(alloc.hours).toFixed(0)}
                          {t("allocations.table.hoursShort")}
                        </span>
                      </span>
                    </div>
                  ))}
                  {remaining > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-5 rounded-full shrink-0 bg-grey-200 dark:bg-grey-700" />
                      <span className="text-body-regular-md content-action-neutral truncate">
                        {t("allocations.cards.unallocatedHours", { hours: remaining })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!isReadOnly && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-auto"
                  onClick={() => openWorkerModal(worker.workerId)}>
                  {t("allocations.cards.allocateButton")}
                </Button>
              )}
            </>
          );

          return isReadOnly ? (
            <div
              key={worker.workerId}
              className="p-5 radius-xl surface-base-fill flex flex-col gap-5 cursor-pointer hover:opacity-80 transition-opacity duration-150"
              onClick={() => openWorkerModal(worker.workerId)}
            >
              {cardContent}
            </div>
          ) : (
            <div key={worker.workerId} className="p-5 radius-xl surface-base-fill flex flex-col gap-5">
              {cardContent}
            </div>
          );
        })}
      </div>

      {selectedWorker && (
        <WorkerAllocationModal
          key={selectedWorker.workerId}
          worker={selectedWorker}
          onClose={closeWorkerModal}
          workerProjects={props.workerProjectOptions}
          isLoadingProjects={props.isLoadingWorkerProjects}
          onSave={props.handleBulkSave}
          isSaving={props.isBulkSaving}
          isDraft={props.isDraft}
          canEdit={props.canEdit}
          t={props.t}
        />
      )}
    </>
  );
}

